import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift as GiftIcon, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const inputCls =
  "w-full bg-[#1E2128] border border-[#2E3340] focus:border-amber-500/60 outline-none px-4 py-3.5 text-sm font-medium text-zinc-100 placeholder:text-zinc-500 transition-colors duration-300";

const PRESETS = [100, 250, 500, 1000];

export default function Gift() {
  const [amount, setAmount] = useState(250);
  const [custom, setCustom] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [message, setMessage] = useState("");
  const [paying, setPaying] = useState(false);
  const [checkCode, setCheckCode] = useState("");
  const [balance, setBalance] = useState(null);
  const [checking, setChecking] = useState(false);

  const finalAmount = custom ? Number(custom) : amount;

  const purchase = async () => {
    if (!buyerName.trim() || !buyerEmail.trim()) {
      toast.error("Your name and email are required.");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/vouchers/purchase", {
        amount: finalAmount,
        buyer_name: buyerName,
        buyer_email: buyerEmail,
        message,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  const checkBalance = async (e) => {
    e.preventDefault();
    setChecking(true);
    setBalance(null);
    try {
      const { data } = await api.get(`/vouchers/${encodeURIComponent(checkCode.trim())}`);
      setBalance(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChecking(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0F1012] grain" data-testid="gift-page">
      <header className="border-b border-[#2E3340]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400 hover:text-amber-400 transition-colors"
          >
            ← Back
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.4em] text-amber-500">Gift Vouchers</p>
          <h1 className="font-serif-lux font-normal text-4xl sm:text-5xl text-amber-50 mt-3 leading-tight">
            The gift of <span className="gold-text italic">arrival.</span>
          </h1>
          <p className="mt-6 text-sm text-zinc-300 leading-relaxed max-w-md">
            An AUREUS voucher is spendable credit toward any journey — airport transfers, weddings, evenings,
            roadshows. The code arrives by email the moment your card clears, and it never expires.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Redeemable in the booking form — the fare reduces instantly",
              "Delivered by email within moments of payment",
              "Never expires · fully transferable",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-zinc-400">
                <span className="w-1 h-1 bg-amber-500 inline-block" /> {t}
              </li>
            ))}
          </ul>

          <div className="mt-10 border border-[#2E3340] bg-[#1E2128] p-6">
            <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400">Hold a voucher?</p>
            <form onSubmit={checkBalance} className="mt-3 flex gap-2">
              <input
                value={checkCode}
                onChange={(e) => setCheckCode(e.target.value.toUpperCase())}
                placeholder="GIFT-XXXXXXXX"
                className={inputCls}
                data-testid="voucher-check-input"
                required
              />
              <button
                type="submit"
                disabled={checking}
                data-testid="voucher-check-button"
                className="px-5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40"
                aria-label="Check balance"
              >
                {checking ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              </button>
            </form>
            {balance && (
              <p className="mt-3 text-sm text-emerald-400 font-medium" data-testid="voucher-balance">
                {balance.code} — ${balance.remaining} remaining of ${balance.amount}
              </p>
            )}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="border border-[#2E3340] bg-[#1E2128] p-7 lg:p-9"
          data-testid="gift-purchase-card"
        >
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full border border-amber-500/40 flex items-center justify-center">
              <GiftIcon size={16} className="text-amber-400" />
            </span>
            <p className="font-serif-lux text-2xl text-amber-50">Compose a voucher</p>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-2" data-testid="gift-amount-presets">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => { setAmount(p); setCustom(""); }}
                data-testid={`gift-amount-${p}`}
                className={`py-3 font-serif-lux text-lg border transition-colors duration-300 ${
                  !custom && amount === p ? "bg-amber-500 text-black border-amber-500" : "border-[#2E3340] text-zinc-300 hover:border-amber-500/50"
                }`}
              >
                ${p}
              </button>
            ))}
          </div>
          <input
            type="number"
            min={50}
            max={5000}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Or a bespoke amount ($50 – $5,000)"
            className={`${inputCls} mt-3`}
            data-testid="gift-custom-amount"
          />
          <div className="mt-4 space-y-3">
            <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="gift-buyer-name" />
            <input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} placeholder="Your email — the code is sent here" className={inputCls} data-testid="gift-buyer-email" />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="A note to accompany the gift (optional)"
              className={`${inputCls} resize-none`}
              data-testid="gift-message"
            />
          </div>
          <button
            onClick={purchase}
            disabled={paying}
            data-testid="gift-purchase-button"
            className="mt-6 w-full flex items-center justify-center gap-2 font-mono-lux text-[11px] font-bold uppercase tracking-[0.3em] px-8 py-4 bg-amber-500 text-black hover:bg-amber-300 disabled:opacity-40 transition-colors"
          >
            {paying ? "Preparing secure checkout…" : `Purchase voucher — $${finalAmount || 0}`}
          </button>
          <p className="mt-3 text-[10px] text-zinc-600 text-center">Secure card payment via Stripe · Visa, Mastercard, Amex</p>
        </motion.div>
      </div>
    </main>
  );
}
